import * as Yup from 'yup';
import { ComponentKey } from 'enums/componentKey';
import { ReferentialKey } from 'api/referential';
import { InputsConfig } from 'types/form/inputsConfig';
import { frenchMobileRule, normalizeFrenchMobile } from 'shared/utils/frenchMobile';

/**
 * Bloc « gérant » des formulaires de création d'une personne morale (courtier, compagnie,
 * gestionnaire, MIA, PIM) : le fondateur est le gérant juridique, son mobile reçoit les codes
 * de signature des mandats. Civilité et mobile obligatoires depuis le 2026-09-09 ; le
 * téléphone de l'entreprise, lui, est devenu facultatif et retombe sur ce mobile.
 */
export enum GerantFieldNames {
  Civility = 'gerantCivility',
  Phone = 'gerantPhone',
}

export interface GerantFormValues {
  /** Code de civilité du référentiel (ex. « Mr »), converti en id à l'envoi. */
  [GerantFieldNames.Civility]: string;
  [GerantFieldNames.Phone]: string;
}

export const gerantInputsConfig: InputsConfig<GerantFieldNames> = {
  [GerantFieldNames.Civility]: {
    componentKey: ComponentKey.ReferentialDropdown,
    referentialKey: ReferentialKey.Civility,
    name: GerantFieldNames.Civility,
    required: true,
    label: 'ACTOR_FORM.GERANT.LABELS.CIVILITY',
  },
  [GerantFieldNames.Phone]: {
    componentKey: ComponentKey.InputText,
    name: GerantFieldNames.Phone,
    type: 'tel',
    required: true,
    label: 'ACTOR_FORM.GERANT.LABELS.PHONE',
    placeholder: 'ACTOR_FORM.GERANT.PLACEHOLDERS.PHONE',
  },
};

export const gerantValidationRules = {
  [GerantFieldNames.Civility]: Yup.string().required('La civilité du gérant est requise'),
  [GerantFieldNames.Phone]: frenchMobileRule(
    'Un numéro de mobile français est attendu (06 ou 07)',
    'Le mobile du gérant est requis : il reçoit les codes de signature des mandats'
  ),
};

/** Téléphone de l'entreprise : facultatif, mais au bon format quand il est saisi. */
export const optionalCompanyPhoneRule = Yup.string()
  .transform((value) => (value === '' ? undefined : value))
  .matches(/^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/, 'Format de téléphone invalide')
  .notRequired();

/** Bloc « gerant » des commandes de création, la civilité résolue en id du référentiel. */
export const buildGerantCommand = (
  values: GerantFormValues,
  getReferentialId: (key: ReferentialKey, code?: string) => string | undefined
): { civiliteId: string; phone: string } => ({
  civiliteId: getReferentialId(ReferentialKey.Civility, values[GerantFieldNames.Civility]) ?? '',
  phone: normalizeFrenchMobile(values[GerantFieldNames.Phone]),
});
